import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    ElementRef,
    inject,
    viewChild,
    ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import type { ApiTypes, TrackingPageElementStatusType } from '@hyundai/ng-common-lib';
import {
    DealerFeature,
    getErrorMessage,
    numericValidator,
    RouterLinkDirective,
    ServicesFeature,
    TrackingFeature,
} from '@hyundai/ng-common-lib';
import { GENDER, SERVICECONTACTTABS } from '@hyundai/shared-lib';
import {
    InputRadioComponent,
    InputSelectComponent,
    InputTextareaComponent,
    InputTextComponent,
    M020C060InputCheckboxComponent,
    M040C030BadgeComponent,
    M040C140TabsComponent,
} from '@hyundai/ui';
import { Store } from '@ngrx/store';
import { CHECKOUT_STEP_TOKEN, CheckoutStepComponent } from '@pixelgenau/ngx-checkouts';
import { WidgetInstance } from 'friendly-challenge';

export type ServiceFormContactStepDataEmail = Pick<
    ApiTypes.ServiceFormPayload,
    'email' | 'message' | 'model' | 'numberplate' | 'mileage' | 'firstReg'
>;

export type ServiceFormContactStepDataPhone = Pick<ApiTypes.ServiceFormPayload, 'phoneNumber' | 'contactTime'>;

export type ServiceFormContactStepData = ServiceFormContactStepDataEmail &
    ServiceFormContactStepDataPhone &
    Pick<
        ApiTypes.ServiceFormPayload,
        'gender' | 'firstName' | 'lastName' | 'businessCustomer' | 'contactType' | 'dealer' | 'captchaSolution'
    >;

@Component({
    selector: 'app-service-form-contact-step',
    standalone: true,
    templateUrl: './service-form-contact-step.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        InputTextComponent,
        InputTextareaComponent,
        InputSelectComponent,
        InputRadioComponent,
        M020C060InputCheckboxComponent,
        M040C030BadgeComponent,
        M040C140TabsComponent,
        ReactiveFormsModule,
        RouterLinkDirective,
    ],
    providers: [{ provide: CHECKOUT_STEP_TOKEN, useExisting: ServiceFormContactStepComponent }],
})
export default class ServiceFormContactStepComponent extends CheckoutStepComponent {
    SERVICECONTACTTABS = SERVICECONTACTTABS;
    GENDER = GENDER;
    store = inject(Store);
    dealer = this.store.selectSignal(DealerFeature.state.selectLoadedDealer);
    servicesFormContent = this.store.selectSignal(ServicesFeature.state.selectLoadedServiceForm);
    getErrorMessage = getErrorMessage;
    trackingIndex = 0;

    friendlyCaptcha = viewChild<ElementRef<HTMLElement>>('frccaptcha');

    disclaimer =
        'Die von Ihnen angegebenen personenbezogenen Daten werden durch den zuständigen Vertragshändler allein zur Bearbeitung Ihrer Kontaktanfrage verarbeitet. Weitere Hinweise zur Datenverarbeitung und Ihren Rechten als betroffene Person finden Sie in unserer Datenschutzerklärung unter';

    override form = new FormGroup({
        gender: new FormControl<GENDER>(GENDER.FEMALE, { nonNullable: true }),
        firstName: new FormControl<string>('', {
            nonNullable: true,
            validators: [Validators.required],
        }),
        lastName: new FormControl<string>('', {
            nonNullable: true,
            validators: [Validators.required],
        }),
        businessCustomer: new FormControl<boolean>(false, { nonNullable: true }),
        tab: new FormControl<SERVICECONTACTTABS>(SERVICECONTACTTABS.PHONE, { nonNullable: true }),
        dealer: new FormControl<string | null>(
            { value: null, disabled: true },
            {
                nonNullable: true,
                validators: [Validators.required],
            },
        ),
        phoneNumber: new FormControl<string>('', {
            nonNullable: true,
            validators: [Validators.required, numericValidator()],
        }),
        contactTime: new FormControl<string | null>(null, {
            nonNullable: true,
            validators: [Validators.required],
        }),
        email: new FormControl<string>(
            { value: '', disabled: true },
            {
                nonNullable: true,
                validators: [Validators.required, Validators.email],
            },
        ),
        message: new FormControl<string | null>(null),
        model: new FormControl<string | null>(
            { value: null, disabled: true },
            {
                nonNullable: true,
                validators: [Validators.required],
            },
        ),
        numberplate: new FormControl<string>(
            { value: '', disabled: true },
            {
                nonNullable: true,
                validators: [Validators.required, Validators.maxLength(12)],
            },
        ),
        mileage: new FormControl<number | null>(
            { value: null, disabled: true },
            {
                nonNullable: true,
                validators: [Validators.required, Validators.maxLength(7), numericValidator()],
            },
        ),
        firstReg: new FormControl<string>(
            { value: '', disabled: true },
            {
                nonNullable: true,
                validators: [Validators.required, Validators.maxLength(14)],
            },
        ),
        captchaSolution: new FormControl<string>('', {
            nonNullable: true,
            validators: [Validators.required],
        }),
    });

    dealers = computed(() => {
        const dealer = this.dealer();

        if (dealer.subDealers.length > 0) {
            this.form.controls.dealer.enable();
        }

        return [
            {
                value: dealer.id,
                label: `${[dealer.name, dealer.name2, dealer.name3].filter((n) => !!n.trim()).join(' ')}, ${dealer.city}`,
            },
            ...dealer.subDealers.map((d) => ({
                value: d.id,
                label: `${[d.name, d.name2, d.name3].filter((n) => !!n.trim()).join(' ')}, ${d.city}`,
            })),
        ];
    });

    // eslint-disable-next-line max-lines-per-function
    constructor() {
        super();
        effect(() => {
            const el = this.friendlyCaptcha()?.nativeElement;
            if (this.isActive() && this.friendlyCaptcha() && el) {
                const widget = new WidgetInstance(el, {
                    doneCallback: (solution) => {
                        this.form.get('captchaSolution')?.patchValue(solution);
                        this.form.updateValueAndValidity();
                    },
                    errorCallback: (b) => {
                        console.error('error', b);
                        this.form.get('captchaSolution')?.patchValue('');
                        this.form.updateValueAndValidity();
                    },
                });
                widget.start();
            }
        });
        // eslint-disable-next-line max-statements
        this.form.controls.tab.valueChanges.pipe(takeUntilDestroyed()).subscribe((tab) => {
            let i: undefined | number;
            let label: undefined | string;
            if (tab === SERVICECONTACTTABS.EMAIL) {
                this.form.controls.phoneNumber.disable();
                this.form.controls.contactTime.disable();
                this.form.controls.email.enable();
                this.form.controls.model.enable();
                this.form.controls.numberplate.enable();
                this.form.controls.mileage.enable();
                this.form.controls.firstReg.enable();
                i = 2;
                label = 'Email';
            } else {
                this.form.controls.email.disable();
                this.form.controls.model.disable();
                this.form.controls.numberplate.disable();
                this.form.controls.mileage.disable();
                this.form.controls.firstReg.disable();
                this.form.controls.phoneNumber.enable();
                this.form.controls.contactTime.enable();
                i = 1;
                label = 'Phone';
            }
            if (this.trackingIndex > 0) {
                this.store.dispatch(
                    TrackingFeature.actions.clickTracking({
                        elType: 'tab',
                        elLabel: label,
                        elTarget: 'tab',
                        event: 'element_change',
                        status: `tab_${i}` as TrackingPageElementStatusType,
                    }),
                );
            }
            this.trackingIndex++;
        });
    }

    next(): void {
        this.form.markAllAsTouched();
        super.complete();
    }

    back(): void {
        this.previousStep.emit();
    }

    override showStepperMenu(): boolean {
        return false;
    }

    override showSidebar(): boolean {
        return false;
    }

    override activate(): void {
        this.trackingIndex = 0;
        super.activate();
    }

    override getSubmitData(): ServiceFormContactStepData {
        const genderMapping: { [key in GENDER]: 'Herr' | 'Frau' | 'Divers' } = {
            [GENDER.MALE]: 'Herr',
            [GENDER.FEMALE]: 'Frau',
            [GENDER.DIVERS]: 'Divers',
        };

        const formGender = this.form.value.gender as GENDER;
        const gender: 'Herr' | 'Frau' | 'Divers' = genderMapping[formGender] ?? 'Herr';

        const emailTab: ServiceFormContactStepDataEmail = {
            email: this.form.value.email ?? '',
            message: this.form.value.message ?? '',
            model: this.form.value.model ?? '',
            numberplate: this.form.value.numberplate ?? '',
            firstReg: this.form.value.firstReg ?? '',
            mileage: this.form.value.mileage?.toString() ?? '',
        };

        const phoneTab: ServiceFormContactStepDataPhone = {
            phoneNumber: this.form.value.phoneNumber ?? '',
            contactTime: this.form.value.contactTime === 'Vormittags' ? 'Vormittags' : 'Nachmittags',
        };

        const ret: ServiceFormContactStepData = {
            gender,
            firstName: this.form.value.firstName ?? '',
            lastName: this.form.value.lastName ?? '',
            businessCustomer: this.form.value.businessCustomer ?? false,
            contactType: this.form.value.tab === SERVICECONTACTTABS.EMAIL ? 'Email' : 'Telefon',
            dealer: this.form.value.dealer ?? this.dealer().id,
            captchaSolution: this.form.value.captchaSolution ?? '',

            ...emailTab,
            ...phoneTab,
        };

        return ret;
    }
}
